import React from 'react';

export interface LoadingSpinnerProps {
  /** 尺寸 */
  size?: 'sm' | 'md' | 'lg';
  /** 文字说明 */
  message?: string;
  /** 是否全屏遮罩 */
  fullscreen?: boolean;
  /** 自定义类名 */
  className?: string;
}

const sizeMap = {
  sm: { spinner: 'h-5 w-5', text: 'text-sm' },
  md: { spinner: 'h-8 w-8', text: 'text-base' },
  lg: { spinner: 'h-12 w-12', text: 'text-lg' },
};

/**
 * 星宿主题加载动画 — 太极图旋转 + 金色光芒
 */
const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  message = '加载中...',
  fullscreen = false,
  className = '',
}) => {
  const s = sizeMap[size];
  const inner = (
    <div
      className={`flex flex-col items-center justify-center gap-4 ${className}`}
      role="status"
      aria-label={message}
    >
      {/* 外圈金色光环 */}
      <div className="relative">
        <svg
          className={`animate-spin ${s.spinner} text-brand-accent`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        {/* 内圈星光 */}
        <svg
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse ${size === 'lg' ? 'h-6 w-6' : size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} text-brand-accent-light`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M12 .586l3.707 3.707a1 1 0 01-1.414 1.414L13 4.414V11a1 1 0 11-2 0V4.414L9.707 9.707a1 1 0 01-1.414-1.414L12 .586zM12 23.414l-3.707-3.707a1 1 0 011.414-1.414L11 19.586V13a1 1 0 112 0v6.586l2.293-2.293a1 1 0 011.414 1.414L12 23.414z" />
        </svg>
      </div>

      {message && (
        <p className={`${s.text} text-neutral-300 font-serif-chinese tracking-wide`}>
          {message}
        </p>
      )}
    </div>
  );

  if (fullscreen) {
    return (
      <div className="fixed inset-0 z-[var(--z-modal)] bg-brand-primary/80 backdrop-blur-sm flex items-center justify-center loading-backdrop">
        {inner}
      </div>
    );
  }

  return inner;
};

export default LoadingSpinner;
